/**
 * @module error.service
 * Service for handling and formatting errors throughout the application
 * 
 * This service provides utilities for error handling, formatting, and dispatching.
 * It ensures consistent error behavior across the application and integrates with
 * the Redux error state management.
 * 
 * @see {@link src/store/error/error.slice.ts} - Redux slice for error state
 * @see {@link src/providers/ErrorProvider.tsx} - Provider that displays errors
 * @see {@link src/services/apiService/axiosService.ts} - API error handling
 */
import type { Dispatch } from 'redux';

import { errorActions } from '@/store/error/error.slice';

/**
 * Types for various error structures we might encounter
 */
interface ErrorWithResponse {
  response?: {
    data?: {
      message?: string;
      error?: string;
    } | string;
    status?: number;
    statusText?: string;
  };
  message?: string;
  stack?: string;
}

interface ErrorWithInfo {
  message?: string;
  stack?: string;
  info?: {
    requestId?: string;
    [key: string]: unknown;
  };
}

/**
 * Union type of possible error objects
 */
type AppError = Error | ErrorWithResponse | ErrorWithInfo | string | unknown;

/**
 * Type for our detailed error object structure
 */
interface DetailedError {
  requestId: string;
  message: string;
  timestamp: string;
  source: string;
  details: {
    originalError?: unknown;
    stack?: string;
    [key: string]: unknown;
  };
}

/**
 * Extract a detailed error message from different error types
 * This maintains the original error extraction logic while adding more robust handling
 * @param error - The error object from any source
 * @returns {string} A clean, user-friendly error message
 */
const extractErrorMessage = (error: AppError): string => {
  // Handle different error structures
  if (!error) return 'Unknown error occurred';
  
  // String errors
  if (typeof error === 'string') return error;
  
  // Axios error response structure - Preserving original behavior
  const errorObj = error as ErrorWithResponse;
  if (errorObj.response) {
    // Original error extraction logic:
    // error?.response?.data?.message || error?.response?.data?.error
    if (typeof errorObj.response.data === 'object' && errorObj.response.data !== null) {
      if (errorObj.response.data.message) return errorObj.response.data.message;
      if (errorObj.response.data.error) return errorObj.response.data.error;
    }
    
    // Additional handling for other response data formats
    if (typeof errorObj.response.data === 'string') return errorObj.response.data;
    
    // Fallback to status code description
    return `Server error: ${errorObj.response.status} ${errorObj.response.statusText || ''}`;
  }
  
  // Native Error object - Preserve original behavior
  // Original: error?.message || error
  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  
  // Last resort (original would return error directly if it was an object)
  return typeof error === 'object' ? JSON.stringify(error) : String(error);
};

/**
 * Generate a detailed error object for logging
 * @param error - Any error object
 * @returns {DetailedError} Structured error information for logging
 */
export const createDetailedError = (error: AppError): DetailedError => {
  const errorMessage = extractErrorMessage(error);
  const timestamp = new Date().toISOString();
  
  const errorWithInfo = error as ErrorWithInfo;
  const requestId = errorWithInfo.info?.requestId || `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  return {
    requestId,
    message: errorMessage,
    timestamp,
    source: 'client',
    // Include the original error info if available
    details: errorWithInfo.info || {
      originalError: error,
      stack: typeof error === 'object' && error !== null && 'stack' in error ? error.stack : undefined
    }
  };
};

/**
 * Dispatch an error to the Redux store for display
 * @param error - Any error object or string
 */
export const loadingError = (error: AppError) => (dispatch: Dispatch) => {
  // Get clean error message
  const errorMessage = extractErrorMessage(error);
  
  // Log detailed error for debugging
  const detailedError = createDetailedError(error);
  console.error(`[App Error ${detailedError.requestId}]:`, detailedError);
  
  // Dispatch to Redux
  dispatch(errorActions.loadError(errorMessage));
};

/**
 * Clear the current error from the Redux store
 */
export const clearError = () => (dispatch: Dispatch) => {
  dispatch(errorActions.clearError());
};

/**
 * Log errors without displaying them to the user
 * Useful for non-critical issues that shouldn't interrupt the user
 * @param error - Any error object or string
 * @param context - Optional context information
 */
export const logSilentError = (error: AppError, context?: string): string => {
  const detailedError = createDetailedError(error);
  console.warn(`[Silent Error${context ? `: ${context}` : ''} ${detailedError.requestId}]:`, detailedError);
  
  // Could send to monitoring service here if needed
  return detailedError.requestId; // Return ID for correlation
};
